import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { useState } from 'react';

interface AppliedQueryPanelProps {
  query: string;
}

export function AppliedQueryPanel({ query }: AppliedQueryPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(query);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard errors
    }
  }

  return (
    <div
      className={cn(
        'rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
        'bg-[var(--color-qup-bg-dark)]',
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className={cn(
          'flex w-full items-center justify-between px-4 py-3 text-xs font-semibold',
          'text-[var(--color-qup-text-muted-dark)] hover:text-[var(--color-qup-text-dark)] transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)] focus-visible:ring-inset',
        )}
        aria-expanded={expanded}
      >
        <span>{t('report.applied_query')}</span>
        <svg
          className={cn('h-4 w-4 transition-transform duration-200', expanded && 'rotate-180')}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-[var(--color-qup-border-dark)] px-4 pb-4 pt-3">
          <div className="relative">
            <pre
              className={cn(
                'overflow-x-auto rounded-[var(--radius-input)] bg-[var(--color-qup-bg-card-dark)] p-3',
                'font-mono text-xs text-[var(--color-qup-text-dark)] leading-relaxed',
                'max-h-64 whitespace-pre-wrap break-all',
              )}
            >
              {query}
            </pre>
            <button
              type="button"
              onClick={handleCopy}
              className={cn(
                'absolute right-2 top-2 rounded px-2 py-1 text-xs font-medium transition-all duration-150',
                copied
                  ? 'bg-[var(--color-qup-success)]/15 text-[var(--color-qup-success)]'
                  : 'bg-[var(--color-qup-border-dark)] text-[var(--color-qup-text-muted-dark)] hover:text-[var(--color-qup-text-dark)]',
              )}
            >
              {copied ? t('report.applied_query_copied') : t('report.applied_query_copy')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
